import React, {lazy, ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import type {LanguageCode, ModuleItem, NavigationMenu, VsnAuth, VSNNavigation,} from '@visma-swno/vsn-navigation';
import {useTranslation} from '../translations/useTranslation';
import {HOMEPAGE} from 'utils/constants';
import {
  getLanguage,
  getUsersCurrentContextId,
  getUsersData,
  getUsersLoggedInData,
} from '../store/application.reducers';
import {logoutUser, switchUserContext} from '../usercontext/user.action';
import * as OdpApi from 'utils/api/odp.api';
import {getAppMenuItemsV2} from 'utils/api/api';
import ContextSelector from './ContextSelector.component';
import NavigationSkeleton from './NavigationSkeleton.component';
import {isOverlayVisible} from 'utils/overlayVisibility';
import {launchDarkly} from 'utils/launchDarkly';
import {productName} from 'utils/productName.function';
import {
  ALL_MENU_IDS,
  APPROVAL_MODULE_ID,
  buildModuleTree,
  collectMenuTargets,
  type MenuNode,
  resolveCurrentItem,
  type UserRoles,
} from './VsnNavigation.function';
import {AmaMenuItem} from "types/appMenuItemV2";
import {setMyTasksVersionTrait} from 'utils/survicate.function';

const NavAiAssistant = lazy(() => import('./NavAiAssistant.jsx'));

const NAVIGATION_TAG = 'vsn-navigation';

// Starts at app boot alongside /startup; the tag upgrades in place, so the page inside never waits for it.
import('@visma-swno/vsn-navigation').catch(error => console.error('vsn-navigation failed to load', error));

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'vsn-navigation': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
    }
  }
}

type UserData = {
  userRoles?: UserRoles;
  companyName?: string;
};

type LoggedInData = {
  connectUserId: string;
  firstName?: string;
  lastName?: string;
  emailAddress: string;
};

type Props = {
  children?: ReactNode;
};

const VsnNavigation = ({ children }: Props) => {
  const { translate: translateFn } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector(getUsersData) as UserData | undefined;
  const loggedInData = useSelector(getUsersLoggedInData) as LoggedInData | undefined;
  const currentContextId = useSelector(getUsersCurrentContextId) as number | undefined;
  const language = useSelector(getLanguage) as LanguageCode | undefined;
  const navRef = useRef<VSNNavigation | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDefined, setIsDefined] = useState(() => Boolean(customElements.get(NAVIGATION_TAG)));
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  // mounted on first open so its chunks stay off the initial load, then kept so the chat survives closing
  const [hasOpenedAssistant, setHasOpenedAssistant] = useState(false);
  const closeAssistant = useCallback(() => setIsAssistantOpen(false), []);
  const onContextSelection = useCallback((newContext: number) => {
    dispatch(switchUserContext(newContext));
  }, [dispatch]);

  // Flags the menu resolves before showing an item. Read here rather than inside the tree builder so
  // that stays pure, and so the tree rebuilds when a flag arrives after the first render.
  const apiToolEnabled = launchDarkly.userClient?.variation('fe-api-tool', false) ?? false;

  // Build the menu once; breadcrumb selection and access redirect share it so they can't disagree.
  const moduleTree = useMemo(
      () => buildModuleTree(translateFn, userData?.userRoles, {'fe-api-tool': apiToolEnabled}),
      [translateFn, userData, apiToolEnabled],
  );
  const { visibleLeafUrls, firstLeafUrl } = useMemo(
      () => collectMenuTargets((moduleTree as MenuNode).items ?? []),
      [moduleTree],
  );
  const resolvedCurrent = useMemo(() => resolveCurrentItem(location.pathname), [location.pathname]);

  // Redirect check uses the raw pathname (not the breadcrumb mapping) so detail views
  // like `/task/123` aren't bounced. Only leaf URLs count as visible — parent URLs may
  // render a hidden index child (e.g. /configuration → Substitutes).
  const { currentItem, redirectTo } = useMemo<{ currentItem: string; redirectTo?: string }>(() => {
    const pathname = location.pathname;
    if (!ALL_MENU_IDS.has(pathname) || visibleLeafUrls.has(pathname)) {
      return { currentItem: resolvedCurrent };
    }
    const fallback = firstLeafUrl ?? HOMEPAGE;
    return { currentItem: fallback, redirectTo: fallback };
  }, [visibleLeafUrls, resolvedCurrent, firstLeafUrl, location.pathname]);

  useEffect(() => {
    if (redirectTo && redirectTo !== location.pathname) {
      navigate(redirectTo, { replace: true });
    }
  }, [redirectTo, location.pathname, navigate]);

  const tree = useMemo<NavigationMenu>(() => ({
    items: [moduleTree],
    currentItem,
    search: { enabled: true },
  }), [moduleTree, currentItem]);

  const auth = useMemo<VsnAuth | undefined>(() => loggedInData ? {
    user: {
      userId: loggedInData.connectUserId,
      userName: [loggedInData.firstName, loggedInData.lastName].filter(Boolean).join(' '),
      userEmail: loggedInData.emailAddress,
    },
    profilePictureBaseUrl: window.ENVIRONMENT?.PROFILE_PICTURE_BASE_URL ?? '',
    myProfileUrl: OdpApi.myDetailsUrl(),
  } : undefined, [loggedInData]);

  useEffect(() => {
    if (isDefined) return;
    let active = true;
    customElements.whenDefined(NAVIGATION_TAG).then(() => active && setIsDefined(true));
    return () => {
      active = false;
    };
  }, [isDefined]);

  // Lit picks up properties set before the upgrade, so these don't need to wait for isDefined.
  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;
    navElement.tree = tree;
    navElement.modules = {
      activeModuleId: APPROVAL_MODULE_ID,
      activeModuleLabel: productName(),
      modules: async (): Promise<ModuleItem[]> => {
        let result = await getAppMenuItemsV2();
        try {
          return result.result.map((item: AmaMenuItem) => {
            return { ...item, label: item.name };
          });
        } catch (e) {
          console.log(e);
        }
      }
    };
    navElement.auth = auth;
    if (language) navElement.lang = language;
    navElement.landingPageUrl = window.ENVIRONMENT?.LANDING_PAGE_URL || HOMEPAGE;
    navElement.showHelp = Boolean(window.ENVIRONMENT?.HELP_CENTER_URL);
    navElement.helpUrl = window.ENVIRONMENT?.HELP_CENTER_URL;
    navElement.feedback = {
      workspaceId: window.ENVIRONMENT?.SURVICATE_WORKSPACE_KEY,
      surveyId: window.ENVIRONMENT?.SURVICATE_FEEDBACK_SURVEY_ID,
    };
  }, [tree, auth, language]);

  useEffect(() => {
    const navElement = navRef.current;
    if (!navElement) return;

    const onItemChanged = (e: Event) => {
      const url = (e as CustomEvent<{ url?: string }>).detail?.url;
      if (url && url.startsWith('/')) {
        navigate(url);
      }
    };

    // Module-switcher and landing-page clicks; tree-item clicks come via vsn-menu-item-changed.
    const onNavigate = (e: Event) => {
      e.preventDefault();
      const url = (e as CustomEvent<{ url?: string }>).detail?.url;
      if (!url) return;
      if (url.startsWith('/') && !url.startsWith('//')) {
        navigate(url);
      } else {
        window.location.assign(url);
      }
    };

    const onLogout = () => {
      dispatch(logoutUser());
    };

    const onAssistantClick = () => {
      setHasOpenedAssistant(true);
      setIsAssistantOpen(prev => !prev);
    };

    // fired before the nav opens its Survicate survey, so the reply carries the version it came from
    const onFeedback = () => {
      setMyTasksVersionTrait();
    };

    // vsn-open is the cancelable Ctrl/Cmd+K shortcut request to open the nav
    // search. Suppress it while a modal/popup is open so the menu can't steal
    // focus from (or navigate behind) an active Review/Reject dialog.
    const onVsnOpen = (e: Event) => {
        if (isOverlayVisible()) {
            e.preventDefault();
        }
    };

    navElement.addEventListener('vsn-menu-item-changed', onItemChanged);
    navElement.addEventListener('vsn-navigate', onNavigate);
    navElement.addEventListener('vsn-logout-click', onLogout);
    navElement.addEventListener('vsn-ai-assistant-click', onAssistantClick);
    navElement.addEventListener('vsn-open', onVsnOpen);
    navElement.addEventListener('vsn-feedback', onFeedback);
    return () => {
      navElement.removeEventListener('vsn-menu-item-changed', onItemChanged);
      navElement.removeEventListener('vsn-navigate', onNavigate);
      navElement.removeEventListener('vsn-logout-click', onLogout);
      navElement.removeEventListener('vsn-ai-assistant-click', onAssistantClick);
      navElement.removeEventListener('vsn-open', onVsnOpen);
      navElement.removeEventListener('vsn-feedback', onFeedback);
    };
  }, [navigate, dispatch]);

  return (
      <vsn-navigation ref={navRef as React.Ref<HTMLElement>}>
        {/* the defined element has no "skeleton" slot, so this also stays hidden until react drops it */}
        {!isDefined && <NavigationSkeleton slot="skeleton"/>}
        <div slot="context-selector">
          <ContextSelector
              currentContextId={currentContextId as number}
              companyName={userData?.companyName}
              onSelection={onContextSelection}
              translate={translateFn}
          />
        </div>
        {children}
        {hasOpenedAssistant && (
            <Suspense fallback={null}>
              <NavAiAssistant isOpen={isAssistantOpen} onClose={closeAssistant}/>
            </Suspense>
        )}
      </vsn-navigation>
  );
};

export default VsnNavigation;
